<template>
  <IntercomModal
    v-model:show="showModal"
    :title="isEditMode ? `Edit Invoice ${props.invoice?.invoice_number || ''}` : $t('invoices.createInvoice')"
    width="900px"
  >
    <div class="invoice-form" @keydown.escape="handleEscape">
      <!-- Invoice Date + Invoice Number preview -->
      <div class="form-row">
        <div class="form-field">
          <IntercomDateInput
            v-model="formData.invoice_date"
            :label="$t('invoices.invoiceDate')"
            :max="todayDate"
          />
        </div>
        <div class="form-field">
          <label class="form-label">{{ $t('invoices.invoiceNumber') }}</label>
          <div class="invoice-number-preview">
            <span v-if="isEditMode" class="preview-value">
              {{ props.invoice?.invoice_number || '—' }}
            </span>
            <template v-else>
              <span v-if="isLoadingNextNumber" class="preview-loading">…</span>
              <span v-else class="preview-value">{{ nextInvoiceNumber || '—' }}</span>
              <span class="preview-hint">auto-generated</span>
            </template>
          </div>
        </div>
      </div>

      <!-- Customer & Payment Mode -->
      <div class="form-row">
        <div class="form-field" data-autofocus>
          <IntercomSelect
            ref="customerSelectRef"
            v-model="formData.customer_id"
            :label="$t('invoices.customer')"
            :options="customerSelectOptions"
            placeholder="Walk-in / No Customer"
            filterable
            :disabled="isLoadingCustomers"
            @update:model-value="handleCustomerSelectChange"
            @keydown.enter="onSelectEnter('customer', $event)"
            @search="handleCustomerSearch"
          >
            <template #option="{ option }">
              <span :class="{ 'add-new-option': option.isCreateNew }">{{ option.label }}</span>
            </template>
          </IntercomSelect>

          <!-- Inline New Customer Form -->
          <div v-if="showNewCustomerForm" class="new-customer-form">
            <div class="new-customer-header">
              <span class="new-customer-title">New Customer</span>
            </div>
            <div class="new-customer-fields">
              <IntercomInput
                v-model="newCustomer.name"
                label="Name"
                placeholder="Customer name"
                :disabled="isCreatingCustomer"
              />
              <IntercomPhoneInput
                v-model="newCustomer.phone"
                label="Phone"
                :disabled="isCreatingCustomer"
                @update:valid="(v) => (newCustomerPhoneValid = v)"
              />
              <IntercomInput
                v-model="newCustomer.email"
                label="Email"
                placeholder="Optional"
                :disabled="isCreatingCustomer"
              />
            </div>
            <div class="new-customer-actions">
              <button type="button" class="cancel-inline-btn" @click="cancelNewCustomer">
                Cancel
              </button>
              <IntercomButton
                variant="primary"
                size="sm"
                :loading="isCreatingCustomer"
                :disabled="!newCustomer.name || (newCustomer.phone && !newCustomerPhoneValid)"
                @click="createAndSelectCustomer"
              >
                Create & Select
              </IntercomButton>
            </div>
          </div>
        </div>

        <div class="form-field">
          <IntercomSelect
            ref="paymentModeRef"
            v-model="formData.payment_mode"
            :label="$t('payments.paymentMode')"
            :options="paymentModeOptions"
            @keydown.enter="onSelectEnter('paymentMode', $event)"
          />
        </div>
      </div>

      <!-- Sale Type: Cash / Credit -->
      <div class="form-row single">
        <div class="form-field">
          <label class="form-label">Sale Type</label>
          <div class="sale-type-row">
            <IntercomFilterButton
              v-for="st in SALE_TYPES"
              :key="st.value"
              :active="formData.sale_type === st.value"
              @click="formData.sale_type = st.value"
            >{{ st.label }}</IntercomFilterButton>
          </div>
          <span v-if="formData.sale_type === 'credit' && !formData.customer_id" class="form-hint form-hint--error">
            Customer is required for credit sales
          </span>
        </div>
      </div>

      <!-- Due Date (configurable via settings) -->
      <div v-if="billingSettings.show_due_date !== false" class="form-row single">
        <div class="form-field">
          <IntercomDateInput
            v-model="formData.due_date"
            :label="$t('invoices.dueDate')"
            :min="todayDate"
            @keydown.enter.prevent="advance('dueDate')"
          />
          <span class="form-hint">{{ $t('invoices.dueDateHint') }}</span>
        </div>
      </div>

      <!-- Ship To / Consignee Toggle -->
      <div class="ship-to-toggle">
        <label class="ship-to-label">
          <input type="checkbox" v-model="showShipTo" class="ship-to-checkbox" />
          Ship to a different address
        </label>
      </div>

      <!-- Ship To Fields -->
      <div v-if="showShipTo" class="ship-to-fields">
        <div class="form-row">
          <div class="form-field">
            <IntercomInput
              v-model="formData.consignee_name"
              label="Consignee Name"
              placeholder="Recipient name"
            />
          </div>
          <div class="form-field">
            <IntercomSelect
              v-model="formData.shipping_state_code"
              label="Shipping State"
              :options="stateOptions"
              placeholder="Select state"
              filterable
            />
          </div>
        </div>
        <div class="form-row single">
          <div class="form-field">
            <IntercomInput
              v-model="formData.shipping_address"
              label="Shipping Address"
              placeholder="Delivery address"
            />
          </div>
        </div>
      </div>

      <!-- Line Items Section -->
      <div class="section-divider">
        <span class="section-title">{{ $t('invoices.lineItems') }}</span>
      </div>

      <!-- Line Items Table -->
      <div class="line-items-table">
        <div class="line-items-header">
          <div class="col-product">Product</div>
          <div class="col-qty">Qty</div>
          <div class="col-price">Price</div>
          <div class="col-disc">Disc</div>
          <div class="col-tax">Tax</div>
          <div class="col-total">Total</div>
          <div class="col-actions"></div>
        </div>

        <div
          v-for="(item, index) in formData.items"
          :key="index"
          class="line-item-row"
        >
          <div class="col-product">
            <IntercomSelect
              :ref="(el) => setItemRef(index, 'product', el)"
              v-model="item.product_id"
              :options="productOptions"
              placeholder="Type to search products..."
              filterable
              @update:model-value="(v) => handleProductSelect(index, v)"
              @search="handleProductSearch"
              @keydown.enter="(e) => { if (!e.defaultPrevented) { e.preventDefault(); handleItemEnter(index, 'product') } }"
            />
          </div>

          <div class="col-qty">
            <input
              :ref="(el) => setItemRef(index, 'qty', el)"
              v-model.number="item.quantity"
              type="number"
              :min="getQtyStep(item.unit_type)"
              :step="getQtyStep(item.unit_type)"
              class="item-input"
              placeholder="Qty"
              @input="calculateLineTotal(index)"
              @change="validateItemStock(index)"
              @keydown.enter.prevent="handleItemEnter(index, 'qty')"
              @keydown.left="handleItemArrowLeft(index, 'qty', $event)"
              @keydown.right="handleItemArrowRight(index, 'qty', $event)"
            />
          </div>

          <div class="col-price">
            <input
              :ref="(el) => setItemRef(index, 'price', el)"
              v-model.number="item.unit_price"
              type="number"
              min="0"
              step="0.01"
              class="item-input"
              placeholder="Price"
              @input="calculateLineTotal(index)"
              @keydown.enter.prevent="handleItemEnter(index, 'price')"
              @keydown.left="handleItemArrowLeft(index, 'price', $event)"
              @keydown.right="handleItemArrowRight(index, 'price', $event)"
            />
          </div>

          <div class="col-disc">
            <div class="disc-input-wrap">
              <input
                :ref="(el) => setItemRef(index, 'discount', el)"
                v-model.number="item.discount_value"
                type="number"
                min="0"
                step="0.01"
                class="item-input disc-input"
                placeholder="0"
                @input="calculateLineTotal(index)"
                @keydown.enter.prevent="handleItemEnter(index, 'discount')"
                @keydown.left="handleItemArrowLeft(index, 'discount', $event)"
                @keydown.right="handleItemArrowRight(index, 'discount', $event)"
              />
              <button
                type="button"
                class="disc-toggle"
                @click="toggleDiscountType(index)"
                :title="item.discount_type === 'percentage' ? 'Percentage' : 'Flat amount'"
              >{{ item.discount_type === 'percentage' ? '%' : '₹' }}</button>
            </div>
          </div>

          <div class="col-tax">
            <span class="tax-display">{{ formatTaxRate(item) }}</span>
          </div>

          <div class="col-total">
            <span class="total-display">₹{{ calculateItemTotal(item).toFixed(2) }}</span>
          </div>

          <div class="col-actions">
            <button
              type="button"
              class="remove-btn"
              @click="removeLineItem(index)"
              :disabled="formData.items.length === 1"
            >
              ✕
            </button>
          </div>
        </div>

        <button type="button" class="add-item-btn" @click="addLineItem">
          + Add Item
        </button>
      </div>

      <!-- Invoice-Level Discount -->
      <div class="invoice-discount-section">
        <span class="invoice-discount-label">Invoice Discount:</span>
        <div class="invoice-discount-input">
          <input
            ref="invoiceDiscountRef"
            v-model.number="formData.discount_value"
            type="number"
            min="0"
            step="0.01"
            class="item-input"
            placeholder="0"
            @keydown.enter.prevent="advance('invoiceDiscount')"
          />
          <button
            type="button"
            class="disc-toggle"
            @click="toggleInvoiceDiscountType"
          >{{ formData.discount_type === 'percentage' ? '%' : '₹' }}</button>
        </div>
      </div>

      <!-- Totals Section -->
      <div class="totals-section">
        <div class="totals-row">
          <span class="totals-label">Subtotal:</span>
          <span class="totals-value">₹{{ invoiceTotals.subtotal.toFixed(2) }}</span>
        </div>
        <div v-if="invoiceTotals.itemDiscount > 0" class="totals-row discount-row">
          <span class="totals-label">Item Discounts:</span>
          <span class="totals-value">-₹{{ invoiceTotals.itemDiscount.toFixed(2) }}</span>
        </div>
        <div v-if="invoiceTotals.cgst > 0" class="totals-row">
          <span class="totals-label">CGST:</span>
          <span class="totals-value">₹{{ invoiceTotals.cgst.toFixed(2) }}</span>
        </div>
        <div v-if="invoiceTotals.sgst > 0" class="totals-row">
          <span class="totals-label">SGST:</span>
          <span class="totals-value">₹{{ invoiceTotals.sgst.toFixed(2) }}</span>
        </div>
        <div v-if="invoiceTotals.igst > 0" class="totals-row">
          <span class="totals-label">IGST:</span>
          <span class="totals-value">₹{{ invoiceTotals.igst.toFixed(2) }}</span>
        </div>
        <div v-if="invoiceTotals.invoiceDiscount > 0" class="totals-row discount-row">
          <span class="totals-label">Invoice Discount:</span>
          <span class="totals-value">-₹{{ invoiceTotals.invoiceDiscount.toFixed(2) }}</span>
        </div>
        <div class="totals-row grand-total">
          <span class="totals-label">Grand Total:</span>
          <span class="totals-value">₹{{ invoiceTotals.grandTotal.toFixed(2) }}</span>
        </div>
      </div>
    </div>

    <template #footer>
      <IntercomButton variant="secondary" @click="handleCancel">
        {{ $t('common.cancel') }}
      </IntercomButton>
      <IntercomButton
        variant="primary"
        :loading="isSaving"
        @click="handleSaveAndFinalize"
        :disabled="!canSubmit"
      >
        {{ isEditMode ? 'Save Changes' : 'Save & Finalize' }}
      </IntercomButton>
    </template>
  </IntercomModal>
</template>

<script setup>
import { ref, reactive, watch, computed, nextTick, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useToast } from '@shared/composables/useToast.js'
import { useFormKeyboard } from '@shared/composables/useFormKeyboard.js'
import { IntercomModal, IntercomButton, IntercomInput, IntercomDateInput, IntercomPhoneInput, IntercomSelect, IntercomFilterButton } from '@shared/components/intercom'
import { useBilling } from '../composables/useBilling.js'
import { INVOICE_PAYMENT_MODES, SALE_TYPES } from '../utils/billingUtils.js'

const props = defineProps({
  show: Boolean,
  invoice: { type: Object, default: null },
})

const emit = defineEmits(['update:show', 'success'])

const { t } = useI18n()
const toast = useToast()
const { listCustomers, listProducts, createInvoice, updateInvoice, finalizeInvoice, createCustomer, getBillingSettings, peekNextInvoiceNumber } = useBilling()
const billingSettings = ref({})

// Edit mode
const isEditMode = computed(() => !!props.invoice)

// State
const showModal = ref(props.show)
const isLoadingCustomers = ref(false)
const isLoadingProducts = ref(false)
const isSaving = ref(false)
const customerOptions = ref([])
const productOptions = ref([])
const allProducts = ref([])
const showShipTo = ref(false)
const nextInvoiceNumber = ref('')
const isLoadingNextNumber = ref(false)

// --- Keyboard navigation ---
const customerSelectRef = ref(null)
const paymentModeRef = ref(null)
const dueDateRef = ref(null)
const invoiceDiscountRef = ref(null)

// Refs for line item fields: itemRefs[index] = { product, qty, price, discount }
const itemRefs = ref({})
const setItemRef = (index, field, el) => {
  if (!itemRefs.value[index]) itemRefs.value[index] = {}
  itemRefs.value[index][field] = el
}

const { field: kbField, advance, moveLeft, moveRight, handleEscape, clearFields, focusFirst } = useFormKeyboard({
  onSubmit: () => { if (canSubmit.value) handleSaveAndFinalize() },
  onEscape: () => { showModal.value = false },
})

// Re-register fields when items change
const registerKeyboardFields = () => {
  clearFields()
  kbField('customer', customerSelectRef)
  kbField('paymentMode', paymentModeRef)
  if (billingSettings.value?.show_due_date !== false) {
    kbField('dueDate', dueDateRef)
  }
  // Line items
  formData.items.forEach((_, i) => {
    const refs = itemRefs.value[i] || {}
    kbField(`item-${i}-product`, ref(refs.product), { type: 'select' })
    kbField(`item-${i}-qty`, ref(refs.qty), { type: 'number', row: `item-${i}` })
    kbField(`item-${i}-price`, ref(refs.price), { type: 'number', row: `item-${i}` })
    kbField(`item-${i}-discount`, ref(refs.discount), { type: 'number', row: `item-${i}` })
  })
  kbField('invoiceDiscount', invoiceDiscountRef, { type: 'number' })
}

// Handle Enter on IntercomSelect: advance when dropdown is closed (event not prevented)
const onSelectEnter = (fieldName, e) => {
  if (!e.defaultPrevented) {
    e.preventDefault()
    advance(fieldName)
  }
}

// Helper for line item Enter/Arrow handlers
const handleItemEnter = (index, field) => advance(`item-${index}-${field}`)
const handleItemArrowLeft = (index, field, e) => moveLeft(`item-${index}-${field}`, e)
const handleItemArrowRight = (index, field, e) => moveRight(`item-${index}-${field}`, e)

// Indian GST state options for shipping state dropdown
const stateOptions = [
  { value: '01', label: '01 - Jammu & Kashmir' },
  { value: '02', label: '02 - Himachal Pradesh' },
  { value: '03', label: '03 - Punjab' },
  { value: '04', label: '04 - Chandigarh' },
  { value: '05', label: '05 - Uttarakhand' },
  { value: '06', label: '06 - Haryana' },
  { value: '07', label: '07 - Delhi' },
  { value: '08', label: '08 - Rajasthan' },
  { value: '09', label: '09 - Uttar Pradesh' },
  { value: '10', label: '10 - Bihar' },
  { value: '11', label: '11 - Sikkim' },
  { value: '12', label: '12 - Arunachal Pradesh' },
  { value: '13', label: '13 - Nagaland' },
  { value: '14', label: '14 - Manipur' },
  { value: '15', label: '15 - Mizoram' },
  { value: '16', label: '16 - Tripura' },
  { value: '17', label: '17 - Meghalaya' },
  { value: '18', label: '18 - Assam' },
  { value: '19', label: '19 - West Bengal' },
  { value: '20', label: '20 - Jharkhand' },
  { value: '21', label: '21 - Odisha' },
  { value: '22', label: '22 - Chhattisgarh' },
  { value: '23', label: '23 - Madhya Pradesh' },
  { value: '24', label: '24 - Gujarat' },
  { value: '25', label: '25 - Daman & Diu' },
  { value: '26', label: '26 - Dadra & Nagar Haveli' },
  { value: '27', label: '27 - Maharashtra' },
  { value: '28', label: '28 - Andhra Pradesh' },
  { value: '29', label: '29 - Karnataka' },
  { value: '30', label: '30 - Goa' },
  { value: '31', label: '31 - Lakshadweep' },
  { value: '32', label: '32 - Kerala' },
  { value: '33', label: '33 - Tamil Nadu' },
  { value: '34', label: '34 - Puducherry' },
  { value: '35', label: '35 - Andaman & Nicobar' },
  { value: '36', label: '36 - Telangana' },
  { value: '37', label: '37 - Andhra Pradesh (New)' },
  { value: '38', label: '38 - Ladakh' },
]

// Product IDs already on the invoice being edited (exempt from stock check)
const invoiceProductIds = computed(() => {
  if (!props.invoice?.items) return new Set()
  return new Set(props.invoice.items.map(i => i.product_id).filter(Boolean))
})

/**
 * Build product option for dropdown.
 * In edit mode, products already on the invoice are never disabled
 * (their stock was already deducted at finalize).
 */
const toProductOption = (p) => {
  const isOnInvoice = invoiceProductIds.value.has(p.id)
  const outOfStock = p.stock_quantity <= 0 && !isOnInvoice
  return {
    label: outOfStock
      ? `${p.name} - ₹${p.base_price} (Out of stock)`
      : `${p.name} - ₹${p.base_price} (${p.stock_quantity} in stock)`,
    disabled: outOfStock,
    value: p.id,
    product: p,
  }
}

// Payment mode options (from shared config)
const paymentModeOptions = INVOICE_PAYMENT_MODES

// Customer select options with "+ New Customer" sentinel at end
const customerSelectOptions = computed(() => {
  const opts = [
    { value: null, label: 'Walk-in / No Customer' },
    ...customerOptions.value,
    { value: '__create_new__', label: '+ New Customer', isCreateNew: true },
  ]
  return opts
})

// Inline new customer form
const showNewCustomerForm = ref(false)
const isCreatingCustomer = ref(false)
const newCustomerPhoneValid = ref(false)
const newCustomer = reactive({ name: '', phone: '', email: '' })

// Helper: Format date as YYYY-MM-DD for input[type="date"]
const formatDateForInput = (date) => {
  const d = new Date(date)
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

// Computed: Today's date string
const todayDate = computed(() => formatDateForInput(new Date()))

// Computed: Default due date (today + N days). Returns null when due date feature is disabled.
const defaultDueDate = computed(() => {
  if (billingSettings.value?.show_due_date === false) return null
  const days = billingSettings.value?.default_due_days ?? 30
  if (days <= 0) return null
  const date = new Date()
  date.setDate(date.getDate() + days)
  return formatDateForInput(date)
})

// Form data
const formData = reactive({
  customer_id: null,
  sale_type: 'cash',
  payment_mode: 'cash',
  invoice_date: null, // Will be set to today in resetForm
  due_date: null, // Will be set to default in resetForm
  discount_type: 'fixed',
  discount_value: 0,
  consignee_name: null,
  shipping_address: null,
  shipping_state_code: null,
  items: [
    {
      product_id: null,
      quantity: 1,
      unit_price: 0,
      cgst_rate: null,
      sgst_rate: null,
      igst_rate: null,
      hsn_code: null,
      unit_type: null,
      discount_type: 'percentage',
      discount_value: 0,
    },
  ],
})

// Computed: Can submit form
const canSubmit = computed(() => {
  if (formData.items.length === 0) return false
  if (formData.sale_type === 'credit' && !formData.customer_id) return false
  return formData.items.every((item) => {
    return item.product_id && item.quantity > 0 && item.unit_price > 0
  })
})

// Computed: Invoice totals — GST-inclusive (prices include tax, discount applied before tax back-calc)
const invoiceTotals = computed(() => {
  let taxableTotal = 0
  let cgst = 0
  let sgst = 0
  let igst = 0
  let inclusiveTotal = 0
  let totalItemDiscount = 0

  formData.items.forEach((item) => {
    const lineTotal = (item.quantity || 0) * (item.unit_price || 0)

    // Calculate item-level discount (capped: percentage max 100%, fixed max line total)
    let discount = 0
    if (item.discount_value > 0) {
      if (item.discount_type === 'percentage') {
        discount = lineTotal * (Math.min(item.discount_value, 100) / 100)
      } else {
        discount = Math.min(item.discount_value, lineTotal)
      }
    }
    totalItemDiscount += discount
    const effective = lineTotal - discount
    inclusiveTotal += effective

    const combinedRate = (item.cgst_rate || 0) + (item.sgst_rate || 0) + (item.igst_rate || 0)
    if (combinedRate > 0) {
      const taxable = effective / (1 + combinedRate / 100)
      const lineTax = effective - taxable
      taxableTotal += taxable

      if (item.cgst_rate > 0) cgst += lineTax * (item.cgst_rate / combinedRate)
      if (item.sgst_rate > 0) sgst += lineTax * (item.sgst_rate / combinedRate)
      if (item.igst_rate > 0) igst += lineTax * (item.igst_rate / combinedRate)
    } else {
      taxableTotal += effective
    }
  })

  // Invoice-level discount (applied to total after item discounts)
  let invoiceDisc = 0
  if (formData.discount_value > 0) {
    if (formData.discount_type === 'percentage') {
      invoiceDisc = inclusiveTotal * (Math.min(formData.discount_value, 100) / 100)
    } else {
      invoiceDisc = Math.min(formData.discount_value, inclusiveTotal)
    }
  }

  return {
    subtotal: taxableTotal,
    cgst,
    sgst,
    igst,
    itemDiscount: totalItemDiscount,
    invoiceDiscount: invoiceDisc,
    grandTotal: Math.max(0, inclusiveTotal - invoiceDisc),
  }
})

// Initialize on mount — handles v-if case where component is created with show+invoice already set
onMounted(async () => {
  // Fetch billing settings (due date config)
  try {
    const s = await getBillingSettings()
    billingSettings.value = s?.data || s || {}
  } catch { /* defaults apply */ }

  if (props.show) {
    showModal.value = true
    fetchCustomers()
    await fetchProducts()
    if (props.invoice) {
      populateFormFromInvoice(props.invoice)
    }
  }
})

// Watch show prop — handles toggling from parent when component is already mounted
watch(
  () => props.show,
  async (val) => {
    showModal.value = val
    if (val) {
      fetchCustomers()
      await fetchProducts()
      if (props.invoice) {
        populateFormFromInvoice(props.invoice)
      } else {
        resetForm()
        fetchNextInvoiceNumber(formData.invoice_date)
      }
    }
  }
)

// Re-fetch the next invoice number when the user picks a different invoice date.
// FY rollover (Apr 1) changes the prefix, so the counter can shift entirely.
watch(
  () => formData.invoice_date,
  (val) => {
    if (!isEditMode.value && val) {
      fetchNextInvoiceNumber(val)
    }
  }
)

// Watch modal state — focus first field when modal opens
watch(showModal, (val) => {
  emit('update:show', val)
  if (val) {
    nextTick(() => focusFirst())
  }
})

// Re-register keyboard fields when line items change
watch(
  () => formData.items.length,
  () => nextTick(registerKeyboardFields),
  { immediate: true }
)

/**
 * Preview the next auto-generated invoice number for the given date.
 * Best-effort: the number at actual creation may differ by 1 under concurrency.
 */
const fetchNextInvoiceNumber = async (date) => {
  isLoadingNextNumber.value = true
  try {
    const response = await peekNextInvoiceNumber(date)
    nextInvoiceNumber.value = response.invoice_number || response.data?.invoice_number || ''
  } catch (error) {
    console.error('Failed to peek next invoice number:', error)
    nextInvoiceNumber.value = ''
  } finally {
    isLoadingNextNumber.value = false
  }
}

/**
 * Fetch customers for dropdown
 */
const fetchCustomers = async () => {
  isLoadingCustomers.value = true
  try {
    const response = await listCustomers({ limit: 100 })
    customerOptions.value = (response.data || []).map((c) => ({
      label: c.name,
      value: c.id,
    }))
  } catch (error) {
    console.error('Failed to fetch customers:', error)
    customerOptions.value = []
  } finally {
    isLoadingCustomers.value = false
  }
}

/**
 * Fetch products for dropdown
 */
const fetchProducts = async () => {
  isLoadingProducts.value = true
  try {
    const response = await listProducts({ limit: 100 })
    allProducts.value = response.data || []
    productOptions.value = allProducts.value.map(toProductOption)
  } catch (error) {
    console.error('Failed to fetch products:', error)
    productOptions.value = []
  } finally {
    isLoadingProducts.value = false
  }
}

/**
 * Server-side customer search. Debounced. Empty/short query restores the
 * default top-100 list. Keeps the selected customer visible even if absent
 * from search results.
 */
let customerSearchTimer = null
const handleCustomerSearch = (query) => {
  if (customerSearchTimer) clearTimeout(customerSearchTimer)
  if (!query || query.length < 2) {
    fetchCustomers()
    return
  }
  customerSearchTimer = setTimeout(async () => {
    try {
      const response = await listCustomers({ search: query, limit: 20 })
      const results = response.data || []
      const toOpt = (c) => ({ label: c.name, value: c.id })
      const selectedId = formData.customer_id
      const resultIds = new Set(results.map((r) => r.id))
      const selectedOption = selectedId && !resultIds.has(selectedId)
        ? customerOptions.value.find((o) => o.value === selectedId)
        : null
      customerOptions.value = [
        ...(selectedOption ? [selectedOption] : []),
        ...results.map(toOpt),
      ]
    } catch (error) {
      console.error('Customer search failed:', error)
    }
  }, 250)
}

/**
 * Server-side product search — debounced, fetches matching products from API
 */
let productSearchTimer = null
const handleProductSearch = (query) => {
  if (productSearchTimer) clearTimeout(productSearchTimer)
  if (!query || query.length < 2) {
    // Reset to initial list when search cleared
    productOptions.value = allProducts.value.map(toProductOption)
    return
  }
  productSearchTimer = setTimeout(async () => {
    try {
      const response = await listProducts({ search: query, limit: 20 })
      const results = response.data || []
      // Merge with allProducts to keep already-selected items visible
      const existingIds = new Set(allProducts.value.map(p => p.id))
      for (const r of results) {
        if (!existingIds.has(r.id)) {
          allProducts.value.push(r)
        }
      }
      // Always include products already selected in line items so their
      // labels keep rendering while the user searches in another row
      const selectedIds = new Set(
        formData.items.map(i => i.product_id).filter(Boolean)
      )
      const resultIds = new Set(results.map(r => r.id))
      const selectedOptions = allProducts.value
        .filter(p => selectedIds.has(p.id) && !resultIds.has(p.id))
        .map(toProductOption)
      productOptions.value = [...selectedOptions, ...results.map(toProductOption)]
    } catch (error) {
      console.error('Product search failed:', error)
    }
  }, 250)
}

/**
 * Handle product selection - auto-fill price and tax rates
 */
const handleProductSelect = (index, productId) => {
  const item = formData.items[index]
  const pid = productId ?? item.product_id
  if (!pid) return

  const product = allProducts.value.find((p) => p.id === pid)
  if (!product) return

  // Auto-fill unit price
  item.unit_price = product.base_price

  // Auto-fill tax rates from product configuration
  // Default to intra-state (CGST+SGST). Backend determines inter-state (IGST) from customer state code.
  if (product.is_taxable) {
    item.cgst_rate = product.cgst_rate ?? 9.0
    item.sgst_rate = product.sgst_rate ?? 9.0
    item.igst_rate = null // Never set both CGST+SGST and IGST — backend decides based on customer state
  } else {
    item.cgst_rate = null
    item.sgst_rate = null
    item.igst_rate = null
  }

  // Snapshot HSN code and unit type from product
  item.hsn_code = product.hsn_code || null
  item.unit_type = product.unit_type || null

  // Warn if product is out of stock (skip for products already on the invoice being edited)
  const isOnInvoice = invoiceProductIds.value.has(product.id)
  if (product.stock_quantity <= 0 && !isOnInvoice) {
    toast.warning(`${product.name} is out of stock`)
  } else if (item.quantity > product.stock_quantity && !isOnInvoice) {
    toast.warning(`Only ${product.stock_quantity} in stock for ${product.name}`)
  }

  calculateLineTotal(index)
}

/**
 * Calculate line total
 */
const calculateLineTotal = (index) => {
  // Trigger reactivity by accessing the item
  const item = formData.items[index]
  // Totals are computed automatically via reactive properties
}

/**
 * Get quantity step based on unit type.
 * Weight/volume/length units (KGS, GMS, LTR, MLT, MTR, etc.) step by 0.01.
 * Everything else (PCS, BOX, NOS, etc.) steps by 1.
 */
const CONTINUOUS_UNITS = new Set([
  'KGS', 'GMS', 'LTR', 'MLT', 'MTR', 'CMS', 'KME', 'KLR',
  'MTS', 'TON', 'QTL', 'CBM', 'CCM', 'SQM', 'SQF', 'SQY',
  'YDS', 'GYD', 'UGS',
])
const getQtyStep = (unitType) => {
  if (!unitType) return '1'
  return CONTINUOUS_UNITS.has(unitType) ? '0.01' : '1'
}

/**
 * Validate stock when quantity changes
 */
const validateItemStock = (index) => {
  const item = formData.items[index]
  if (!item.product_id) return
  const product = allProducts.value.find(p => p.id === item.product_id)
  if (!product) return
  const isOnInv = invoiceProductIds.value.has(product.id)
  if (product.stock_quantity > 0 && item.quantity > product.stock_quantity && !isOnInv) {
    toast.warning(`Only ${product.stock_quantity} in stock for ${product.name}`)
    item.quantity = product.stock_quantity
  }
}

/**
 * Calculate item total — price is GST-inclusive, discount applied
 */
const calculateItemTotal = (item) => {
  const lineTotal = (item.quantity || 0) * (item.unit_price || 0)
  let discount = 0
  if (item.discount_value > 0) {
    if (item.discount_type === 'percentage') {
      discount = lineTotal * (Math.min(item.discount_value, 100) / 100)
    } else {
      discount = Math.min(item.discount_value, lineTotal)
    }
  }
  return lineTotal - discount
}

/**
 * Toggle discount type between percentage and fixed for a line item
 */
const toggleDiscountType = (index) => {
  const item = formData.items[index]
  item.discount_type = item.discount_type === 'percentage' ? 'fixed' : 'percentage'
  item.discount_value = 0
}

/**
 * Toggle invoice-level discount type
 */
const toggleInvoiceDiscountType = () => {
  formData.discount_type = formData.discount_type === 'percentage' ? 'fixed' : 'percentage'
  formData.discount_value = 0
}

/**
 * Format tax rate display for line item tax column.
 * Shows total GST % with a compact breakdown:
 *   CGST+SGST mode → "18% (9+9)"
 *   IGST mode      → "18% IGST"
 *   Exempt         → "Exempt"
 */
const formatTaxRate = (item) => {
  if (item.cgst_rate != null && item.sgst_rate != null) {
    const total = (item.cgst_rate || 0) + (item.sgst_rate || 0)
    if (total === 0) return 'Exempt'
    return `${total}% (${item.cgst_rate}+${item.sgst_rate})`
  }
  if (item.igst_rate != null && item.igst_rate > 0) {
    return `${item.igst_rate}% IGST`
  }
  return 'Exempt'
}

/**
 * Add new line item
 */
const addLineItem = () => {
  formData.items.push({
    product_id: null,
    quantity: 1,
    unit_price: 0,
    cgst_rate: null,
    sgst_rate: null,
    igst_rate: null,
    hsn_code: null,
    unit_type: null,
    discount_type: 'percentage',
    discount_value: 0,
  })
}

/**
 * Remove line item
 */
const removeLineItem = (index) => {
  if (formData.items.length > 1) {
    formData.items.splice(index, 1)
  }
}

/**
 * Handle customer select change — intercept the sentinel value
 */
const handleCustomerSelectChange = (value) => {
  if (value === '__create_new__') {
    // Reset the select back to null (don't keep '__create_new__' as value)
    nextTick(() => { formData.customer_id = null })
    showNewCustomerForm.value = true
  }
}

/**
 * Cancel inline customer creation
 */
const cancelNewCustomer = () => {
  showNewCustomerForm.value = false
  Object.assign(newCustomer, { name: '', phone: '', email: '' })
  newCustomerPhoneValid.value = false
}

/**
 * Create new customer inline and auto-select them
 */
const createAndSelectCustomer = async () => {
  if (!newCustomer.name.trim()) {
    toast.warning('Customer name is required')
    return
  }
  isCreatingCustomer.value = true
  try {
    // Backend validatePhone expects 10 raw digits (not E.164)
    // IntercomPhoneInput emits "+919876543210" when valid — strip prefix
    let phone = newCustomer.phone || null
    if (phone && phone.startsWith('+91')) phone = phone.slice(3)

    const created = await createCustomer({
      name: newCustomer.name.trim(),
      phone: phone || null,
      email: newCustomer.email || null,
    })
    const customer = created?.customer || created

    // Add to dropdown and auto-select
    customerOptions.value.unshift({ label: customer.name, value: customer.id })
    formData.customer_id = customer.id

    // Hide form and reset
    showNewCustomerForm.value = false
    Object.assign(newCustomer, { name: '', phone: '', email: '' })
    newCustomerPhoneValid.value = false

    toast.success('Customer created')
  } catch (error) {
    console.error('Failed to create customer:', error)
    toast.error('Failed to create customer')
  } finally {
    isCreatingCustomer.value = false
  }
}

/**
 * Populate form from an existing invoice (edit mode)
 */
const populateFormFromInvoice = (inv) => {
  formData.customer_id = inv.customer_id || null
  formData.sale_type = inv.sale_type || 'cash'
  formData.payment_mode = inv.payment_mode || 'cash'
  formData.invoice_date = inv.invoice_date ? formatDateForInput(inv.invoice_date) : todayDate.value
  formData.due_date = inv.due_date ? formatDateForInput(inv.due_date) : defaultDueDate.value
  formData.discount_type = inv.discount_type || 'fixed'
  formData.discount_value = inv.discount_value || 0
  formData.consignee_name = inv.consignee_name || null
  formData.shipping_address = inv.shipping_address || null
  formData.shipping_state_code = inv.shipping_state_code || null
  showShipTo.value = !!(inv.consignee_name || inv.shipping_address || inv.shipping_state_code)
  formData.items = (inv.items || []).map((item) => ({
    product_id: item.product_id,
    quantity: parseFloat(Number(item.quantity).toFixed(3)),
    unit_price: item.unit_price,
    cgst_rate: item.cgst_rate,
    sgst_rate: item.sgst_rate,
    igst_rate: item.igst_rate,
    hsn_code: item.hsn_code,
    unit_type: item.unit_type,
    discount_type: item.discount_type || 'percentage',
    discount_value: item.discount_value || 0,
  }))
  if (formData.items.length === 0) {
    formData.items = [{ product_id: null, quantity: 1, unit_price: 0, cgst_rate: null, sgst_rate: null, igst_rate: null, hsn_code: null, unit_type: null, discount_type: 'percentage', discount_value: 0 }]
  }

  // Inject invoice's products into options if they weren't in the paginated fetch
  for (const item of (inv.items || [])) {
    if (!item.product_id) continue
    const exists = allProducts.value.some((p) => p.id === item.product_id)
    if (!exists) {
      const product = {
        id: item.product_id,
        name: item.product_name || 'Unknown Product',
        base_price: item.unit_price,
        stock_quantity: item.quantity,
        hsn_code: item.hsn_code,
        is_taxable: !!(item.cgst_rate || item.sgst_rate || item.igst_rate),
        cgst_rate: item.cgst_rate || 0,
        sgst_rate: item.sgst_rate || 0,
      }
      allProducts.value.push(product)
      productOptions.value.push(toProductOption(product))
    }
  }
}

/**
 * Reset form
 */
const resetForm = () => {
  showNewCustomerForm.value = false
  Object.assign(newCustomer, { name: '', phone: '', email: '' })
  formData.customer_id = null
  formData.sale_type = 'cash'
  formData.payment_mode = 'cash'
  formData.invoice_date = todayDate.value
  formData.due_date = defaultDueDate.value
  formData.discount_type = 'fixed'
  formData.discount_value = 0
  formData.consignee_name = null
  formData.shipping_address = null
  formData.shipping_state_code = null
  showShipTo.value = false
  formData.items = [
    {
      product_id: null,
      quantity: 1,
      unit_price: 0,
      cgst_rate: null,
      sgst_rate: null,
      igst_rate: null,
      hsn_code: null,
      unit_type: null,
      discount_type: 'percentage',
      discount_value: 0,
    },
  ]
}

/**
 * Handle cancel
 */
const handleCancel = () => {
  showModal.value = false
  resetForm()
}

/**
 * Handle save and finalize
 */
const handleSaveAndFinalize = async () => {
  if (!canSubmit.value) {
    return
  }

  isSaving.value = true

  try {
    // Prepare invoice data
    const invoiceData = {
      customer_id: formData.customer_id,
      sale_type: formData.sale_type,
      payment_mode: formData.payment_mode,
      invoice_date: formData.invoice_date || null,
      due_date: formData.due_date,
      discount_type: formData.discount_value > 0 ? formData.discount_type : null,
      discount_value: formData.discount_value || 0,
      consignee_name: showShipTo.value ? (formData.consignee_name || null) : null,
      shipping_address: showShipTo.value ? (formData.shipping_address || null) : null,
      shipping_state_code: showShipTo.value ? (formData.shipping_state_code || null) : null,
      items: formData.items.map((item) => ({
        product_id: item.product_id,
        quantity: item.quantity,
        unit_price: item.unit_price,
        cgst_rate: item.cgst_rate,
        sgst_rate: item.sgst_rate,
        igst_rate: item.igst_rate,
        hsn_code: item.hsn_code,
        unit_type: item.unit_type,
        discount_type: item.discount_value > 0 ? item.discount_type : null,
        discount_value: item.discount_value || 0,
      })),
    }

    let invoiceId

    if (isEditMode.value) {
      // Edit mode: update existing invoice
      const updateRes = await updateInvoice(props.invoice.id, invoiceData)
      invoiceId = props.invoice.id

      // Show low stock warnings if any
      const warnings = updateRes.low_stock_warnings || updateRes.data?.low_stock_warnings
      if (warnings && warnings.length > 0) {
        const names = warnings.map(w => `${w.product_name} (${w.stock_quantity} left)`).join(', ')
        toast.warning(`Low stock: ${names}`, { duration: 6000 })
      }

      toast.success('Invoice updated successfully')
    } else {
      // Create mode: create draft + finalize
      const createResponse = await createInvoice(invoiceData)
      invoiceId = createResponse.invoice?.id || createResponse.data?.id

      if (!invoiceId) {
        throw new Error('Invoice ID not returned from server')
      }

      // Finalize invoice (triggers stock decrease)
      const finalizeRes = await finalizeInvoice(invoiceId)

      // Show low stock warnings if any products fell below threshold
      const warnings = finalizeRes.low_stock_warnings || finalizeRes.data?.low_stock_warnings
      if (warnings && warnings.length > 0) {
        const names = warnings.map(w => `${w.product_name} (${w.stock_quantity} left)`).join(', ')
        toast.warning(`Low stock: ${names}`, { duration: 6000 })
      }
    }

    // Close modal and emit success with invoice ID
    showModal.value = false
    resetForm()
    emit('success', invoiceId)
  } catch (error) {
    console.error('Failed to save invoice:', error)
    toast.error('Failed to save invoice: ' + (error.message || 'Unknown error'))
  } finally {
    isSaving.value = false
  }
}
</script>

<style scoped>
.invoice-form {
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-4);
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.form-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-charcoal);
}

.form-input {
  width: 100%;
  height: 44px;
  padding: 0 var(--space-3);
  font-size: var(--font-size-base);
  color: var(--color-charcoal);
  background: var(--color-gray-50);
  border: 1px solid var(--color-gray-200);
  border-radius: var(--radius-md);
  outline: none;
  transition: all var(--transition-fast);
}

.form-input:focus {
  background: var(--color-white);
  border-color: var(--color-blue);
  box-shadow: 0 0 0 3px rgba(0, 87, 255, 0.1);
}

.form-hint {
  font-size: var(--font-size-xs);
  color: var(--color-gray-500);
  margin-top: var(--space-1);
}

.ship-to-toggle {
  margin-top: calc(var(--space-2) * -1);
}

.ship-to-label {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  font-size: var(--font-size-sm);
  color: var(--color-charcoal);
  cursor: pointer;
  user-select: none;
}

.ship-to-checkbox {
  width: 16px;
  height: 16px;
  accent-color: var(--color-blue);
  cursor: pointer;
}

.ship-to-fields {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  padding: var(--space-4);
  background: var(--color-gray-50);
  border: 1px solid var(--color-gray-200);
  border-radius: var(--radius-md);
}

.form-hint--error {
  color: var(--color-red, #dc2626);
}

.invoice-number-preview {
  display: flex;
  align-items: baseline;
  gap: var(--space-2, 6px);
  min-height: 38px;
  padding: 8px 12px;
  background: var(--color-gray-50, #f9fafb);
  border: 1px solid var(--color-gray-200, #e5e7eb);
  border-radius: var(--radius-md, 6px);
}

.invoice-number-preview .preview-value {
  font-family: var(--font-mono, ui-monospace, SFMono-Regular, monospace);
  font-size: 14px;
  font-weight: 600;
  color: var(--color-gray-900, #111827);
}

.invoice-number-preview .preview-hint {
  font-size: 12px;
  color: var(--color-gray-500, #6b7280);
}

.invoice-number-preview .preview-loading {
  font-size: 14px;
  color: var(--color-gray-500, #6b7280);
}

.sale-type-row {
  display: flex;
  gap: var(--space-2, 6px);
}

.form-row.single {
  grid-template-columns: 1fr;
  max-width: 300px;
}

.section-divider {
  position: relative;
  text-align: center;
  margin: var(--space-2) 0;
}

.section-divider::before {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  height: 1px;
  background: var(--color-gray-200);
}

.section-title {
  position: relative;
  display: inline-block;
  padding: 0 var(--space-3);
  background: var(--color-white);
  color: var(--color-gray-600);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.line-items-table {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.line-items-header {
  display: grid;
  grid-template-columns: 1fr 70px 70px 90px 80px 90px 36px;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  background: var(--color-gray-100);
  border-radius: var(--radius-md);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  color: var(--color-gray-600);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.line-item-row {
  display: grid;
  grid-template-columns: 1fr 70px 70px 90px 80px 90px 36px;
  gap: var(--space-2);
  align-items: center;
  padding: var(--space-2) var(--space-3);
  background: var(--color-gray-50);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-gray-200);
}

.col-product {
  min-width: 0;
  overflow: hidden;
}

.col-product :deep(.n-base-selection-label) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.col-hsn { text-align: center; }

.col-total { text-align: right; }

.item-input {
  width: 100%;
  height: 36px;
  padding: 0 var(--space-2);
  font-size: var(--font-size-sm);
  color: var(--color-charcoal);
  background: var(--color-white);
  border: 1px solid var(--color-gray-200);
  border-radius: var(--radius-sm);
  outline: none;
  transition: all var(--transition-fast);
}

.item-input:focus {
  border-color: var(--color-blue);
  box-shadow: 0 0 0 2px rgba(0, 87, 255, 0.1);
}

.item-input[type='number'] {
  text-align: right;
}

.tax-display,
.total-display {
  font-size: var(--font-size-sm);
  color: var(--color-charcoal);
  font-weight: var(--font-weight-medium);
}

.remove-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: var(--radius-sm);
  color: var(--color-gray-500);
  cursor: pointer;
  transition: all var(--transition-fast);
  font-size: 16px;
}

.remove-btn:hover:not(:disabled) {
  background: #fee;
  color: #d03050;
}

.remove-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.add-item-btn {
  align-self: flex-start;
  padding: var(--space-2) var(--space-4);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-blue);
  background: transparent;
  border: 1px dashed var(--color-gray-300);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.add-item-btn:hover {
  background: rgba(0, 87, 255, 0.05);
  border-color: var(--color-blue);
}

/* Discount column */
.col-disc {
  min-width: 0;
}

.disc-input-wrap {
  display: flex;
  align-items: center;
  gap: 0;
  border: 1px solid var(--color-gray-200);
  border-radius: var(--radius-sm);
  overflow: hidden;
  background: var(--color-white);
}

.disc-input {
  border: none !important;
  border-radius: 0 !important;
  height: 32px !important;
  width: 50px;
  min-width: 0;
}

.disc-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 32px;
  background: var(--color-gray-100);
  border: none;
  border-left: 1px solid var(--color-gray-200);
  cursor: pointer;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  color: var(--color-gray-600);
  transition: all var(--transition-fast);
  flex-shrink: 0;
}

.disc-toggle:hover {
  background: var(--color-gray-200);
  color: var(--color-charcoal);
}

/* Invoice-level discount */
.invoice-discount-section {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--space-3);
  margin-top: var(--space-3);
  margin-bottom: var(--space-3);
}

.invoice-discount-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-gray-600);
}

.invoice-discount-input {
  display: flex;
  align-items: center;
  gap: 0;
  border: 1px solid var(--color-gray-200);
  border-radius: var(--radius-sm);
  overflow: hidden;
  background: var(--color-white);
  width: 140px;
}

.invoice-discount-input .item-input {
  border: none !important;
  height: 36px;
}

.invoice-discount-input .disc-toggle {
  height: 36px;
}

/* Discount row in totals */
.totals-row.discount-row .totals-value {
  color: var(--color-green-600, #16a34a);
}

.totals-section {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-4);
  background: var(--color-gray-50);
  border-radius: var(--radius-md);
  align-self: flex-end;
  min-width: 300px;
}

.totals-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--space-2) 0;
}

.totals-label {
  font-size: var(--font-size-sm);
  color: var(--color-gray-600);
  font-weight: var(--font-weight-medium);
}

.totals-value {
  font-size: var(--font-size-base);
  color: var(--color-charcoal);
  font-weight: var(--font-weight-semibold);
}

.totals-row.grand-total {
  border-top: 2px solid var(--color-gray-300);
  padding-top: var(--space-3);
  margin-top: var(--space-2);
}

.totals-row.grand-total .totals-label {
  font-size: var(--font-size-base);
  color: var(--color-charcoal);
  font-weight: var(--font-weight-bold);
}

.totals-row.grand-total .totals-value {
  font-size: var(--font-size-lg);
  color: var(--color-blue);
  font-weight: var(--font-weight-bold);
}

/* Sentinel "+ New Customer" option in IntercomSelect dropdown */
.add-new-option {
  color: var(--color-blue);
  font-weight: var(--font-weight-medium);
}

.new-customer-form {
  margin-top: var(--space-3);
  padding: var(--space-4);
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: var(--radius-lg);
}

.new-customer-header {
  margin-bottom: var(--space-3);
}

.new-customer-title {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: #1d4ed8;
}

.new-customer-fields {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: var(--space-3);
  margin-bottom: var(--space-3);
}

.new-customer-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-2);
  padding-top: var(--space-3);
  border-top: 1px solid #bfdbfe;
}

.cancel-inline-btn {
  padding: var(--space-2) var(--space-3);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-gray-600);
  background: transparent;
  border: 1px solid var(--color-gray-300);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.cancel-inline-btn:hover {
  background: var(--color-gray-50);
}

@media (max-width: 640px) {
  .new-customer-fields {
    grid-template-columns: 1fr;
  }
}
</style>
